/** @jsx tsx */
import { tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import { Keys, ShapeType } from "../interfaces/model/enumerations/index";
import { CanvasMode } from "../interfaces/model/enumerations/CanvasMode";
import { ISelection } from "../interfaces/model/Selection";
import { IPageRenderProps } from "./CanvasPage";
import CreateConnectionShape from "./CreateConnectionShape";
import { CreateCustomShape } from "./CreateCustomShape";
import CreatePenShape from "./CreatePenShape";
import { CreatePresetShape } from "./CreatePresetShape";
import { allowMultiShapeSelection } from "../keyUtil";
import { IPosition } from "../meta/Drag/CsiDragController";
import { CsiDrag, IDragResults } from "../meta/Drag/index";
import { getZoomValue } from "../selectors/index";
import { DragSelectionBox, IDragBoxProperties } from "./DragSelectionBox";
import PageHelper from "canvas/store/PageHelper";
import { adjustPositionForRoot } from "../util";

/**
 * Page render properties
 */
export interface ICreateShapeRenderProps extends IPageRenderProps {
    selection: ISelection;
}
/**
 * Canvas Draw page.
 * Provides base component for drawing shapes on canvas. Here we use optimistic rendering,
 * a shape is rendered before adding to the state. This way user can see shape before it get's added,
 * as well it will get added in single operation.
 * Here we will fork into child widgets for Preset or Custom shape depending on the shape type.
 */
export class CanvasCreateShape extends WidgetBase<ICreateShapeRenderProps> {
	/** Drag selection box properties */
	private dragSelectionProps: IDragBoxProperties | null = null;

	private _invertDragX = false;
	private _invertDragY = false;
	private _drawStartPosition?: IPosition;


	constructor() {
		super();
		document.addEventListener("pointerup", this._onCanvasPointerUp.bind(this));
	}
    

	/**
     * Pointer drag event for canvas page
     * @param drag {DragResults}
     */
	protected _onCanvasPointerDrag(drag: IDragResults) {
		if (!drag.isDragging) {
			return;
		}
		// check if drag is inverted as per start position
		if (drag.current && (drag.current.x || drag.current.y) && this._drawStartPosition && this.dragSelectionProps) {
			this._invertDragX = this._drawStartPosition.x > drag.current.x;
			this._invertDragY = this._drawStartPosition.y > drag.current.y;

			this.dragSelectionProps.flipH = this._invertDragX;
			if (this._invertDragX) {
				this.dragSelectionProps.x = drag.current.x;
				this.dragSelectionProps.width = this._drawStartPosition.x - drag.current.x;
			} else {
				this.dragSelectionProps.width = drag.current.x - this._drawStartPosition.x;
			}

			if (this._invertDragY) {
				// in case of invert Y, we will flipH & rotate by 180
				this.dragSelectionProps.flipH = !this.dragSelectionProps.flipH;
				this.dragSelectionProps.rot = 180;

				this.dragSelectionProps.y = drag.current.y;
				this.dragSelectionProps.height = this._drawStartPosition.y - drag.current.y;
			} else {
				this.dragSelectionProps.rot = 0;
				this.dragSelectionProps.height = drag.current.y - this._drawStartPosition.y;
			}
		}
	}

	/**
     * Canvas editor pointer down
     * @param event {PointerEvent}
     */
	protected _onCanvasPointerDown(event: PointerEvent) {
		const {
			api,
			canvasEditor,
			canvasMode,
		} = this.properties;

		const {
			canvasApi,
			canvasContainerEl,
			selectionApi,
		} = api;

		const disableSelectBox = !!PageHelper.getPanningMode();

		if (PageHelper.isDragTypeInstanceMode()) {
			canvasApi.setCanvasMode(CanvasMode.SELECT);
		}

		const t = event.target as HTMLElement;
        
		if (!disableSelectBox &&
            canvasEditor.isEditEnabled &&
            (t.id === "canvas-editor-root" || t.id === "canvas-draw-layer")) {
			if (!allowMultiShapeSelection(event)) {
				// if user click's on canvas while in edit curve mode,
				// curve should get selected instead of deselecting  it
				if (canvasMode !== CanvasMode.EDIT_CURVE) {
					selectionApi.deselectAllObjects();
				}
				canvasApi.setCanvasMode(CanvasMode.SELECT);
				canvasApi.setFormatPainter(false);
				selectionApi.setFormatPainterTextStyles(null, null);
			}
			// drag selection box
			if (canvasMode !== CanvasMode.EDIT_CURVE) {
				this._invertDragX = false;
				this._invertDragY = false;
				const boundingBoxBound = canvasContainerEl.getBoundingClientRect();
				this._drawStartPosition = {
					x: getZoomValue(event.clientX - boundingBoxBound.left),
					y: getZoomValue(event.clientY - boundingBoxBound.top),
				};
				this.dragSelectionProps = {
					flipH: false,
					height: 0,
					rot: 0,
					width: 0,
					x: this._drawStartPosition.x,
					y: this._drawStartPosition.y,
				};
			}
		}
	}

	/**
     * Remove all event listeners on canvas page
     */
	protected onDetach(): void {
		document.removeEventListener("pointerup", this._onCanvasPointerUp.bind(this));
		this.meta(CsiDrag)
			.remove("canvas-editor-key");
	}

	/**
     * Canvas editor pointer up event
     * @param event {PointerEvent}
     */
	protected _onCanvasPointerUp() {
		if (this.dragSelectionProps) {
			adjustPositionForRoot(this.properties.api.canvasContainerEl, this.dragSelectionProps);
			this.properties.api.selectionApi.selectObjectByBox(this.dragSelectionProps);
			this.properties.api.selectionApi.setAllAspectRatios();
			(window as any).placeholderTarget = null;
		}
		this.dragSelectionProps = null;
		this._drawStartPosition = undefined;
	}

	/**
     * Override WidgetBase#render to produce a virtual DOM tree.
     * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
     */
	protected render() {
		if (this.properties.canvasEditor.mode === CanvasMode.DRAW) {
			const shapeDrawTools = this.properties.selection.shapeDrawTools;

			// in case of custom shape, shape type will be undefined.
			if (shapeDrawTools && shapeDrawTools.shapeType) {
				// add special handling for Line shape.
				if (shapeDrawTools.shapeType === ShapeType.line) {
					return <CreateConnectionShape {...this.properties} />;
				} else if (shapeDrawTools.shapeType as any === "pen") {
					return <CreatePenShape {...this.properties} />;
				}
				return <CreatePresetShape {...this.properties} />;
			}
			return <CreateCustomShape {...this.properties} />;
		}
		const dragResult: IDragResults = this.meta(CsiDrag)
			.get("canvas-draw-key");
		this._onCanvasPointerDrag(dragResult);
		return (
			<div
				id="canvas-draw-layer"
				key="canvas-draw-key"
				onpointerdown={this._onCanvasPointerDown}
				styles={{
					position: "absolute",
					top: "0",
					right: "0",
					bottom: "0",
					left: "0",
				}}
			>
				{this.dragSelectionProps && <DragSelectionBox boxProps={{ ...this.dragSelectionProps }} />}
			</div>
		);
	}
}

export default CanvasCreateShape;
